<mat-card class="client-deactivation">
  <mat-card-header>
    <mat-card-title>Client Status</mat-card-title>
    <mat-card-subtitle>{{ isActive ? 'Deactivate this client and unenroll its patients' : 'Activate this client again' }}</mat-card-subtitle>
  </mat-card-header>
  <mat-card-content>
    <div class="client-deactivation__status">
      <mat-icon
        class="client-deactivation__status-icon"
        [class.client-deactivation__status-icon--active]="isActive"
        [class.client-deactivation__status-icon--inactive]="!isActive"
      >
        {{ isActive ? 'check_circle' : 'do_not_disturb_on' }}
      </mat-icon>
      <span>{{ isActive ? 'This client is active.' : 'This client is deactivated.' }}</span>
    </div>

    <p class="client-deactivation__warning" *ngIf="isActive && !isRunning && !hasFailedRun">
      Deactivating {{ client?.client_name }} unenrolls its patients from their care management programs. Review the
      patient count in the confirmation before you continue.
    </p>

    <!-- The run keeps going on the server, so leaving this screen does not stop it. -->
    <div class="client-deactivation__progress" *ngIf="isRunning && !pollUnavailable">
      <p class="client-deactivation__progress-label">
        Unenrolling patients — {{ run?.patientsProcessed }} of {{ run?.total }} ({{ run?.reference }})
      </p>
      <mat-progress-bar mode="determinate" [value]="progressPercent"></mat-progress-bar>
      <p class="client-deactivation__hint">
        This keeps running if you close this page. The client is deactivated once every patient is done.
      </p>
    </div>

    <!-- Shown instead of the bar once the card has stopped being able to read the run. Saying the
         run continues matters: the number above is the last one we could confirm, not a stall. -->
    <div class="client-deactivation__progress" *ngIf="pollUnavailable">
      <p class="client-deactivation__progress-label">
        Progress unavailable{{ run?.reference ? ' (' + run?.reference + ')' : '' }}
      </p>
      <mat-progress-bar mode="determinate" [value]="progressPercent" [class.client-deactivation__bar--stale]="true"></mat-progress-bar>
      <p class="client-deactivation__hint">
        The run continues on the server; this page just stopped being able to check on it. Reload to
        pick it up again.
      </p>
    </div>

    <div class="client-deactivation__failure" *ngIf="hasFailedRun">
      <mat-icon class="client-deactivation__status-icon--inactive">error_outline</mat-icon>
      <span>{{ run?.failureReason }}</span>
    </div>

    <!-- Read from the client document, not from the run: it is stamped in the same write as the
         flag, so it is there on a page load long after any run record was last polled. -->
    <dl class="client-deactivation__stamp" *ngIf="!isActive && stamp">
      <div class="client-deactivation__stamp-row" *ngIf="stamp.deactivated_at">
        <dt>Deactivated</dt>
        <dd>{{ deactivatedAt | date: 'medium' }}</dd>
      </div>
      <div class="client-deactivation__stamp-row" *ngIf="stamp.deactivated_by_name || stamp.deactivated_by">
        <dt>By</dt>
        <dd>{{ stamp.deactivated_by_name || stamp.deactivated_by }}</dd>
      </div>
      <div class="client-deactivation__stamp-row" *ngIf="stamp.patients_unenrolled !== undefined">
        <dt>Patients unenrolled</dt>
        <dd>{{ stamp.patients_unenrolled }}{{ stamp.reference ? ' (' + stamp.reference + ')' : '' }}</dd>
      </div>
    </dl>

    <!-- Once active again, the deactivation stamp above is hidden, so this is where who brought the
         client back — and when it was last terminated — stays visible. -->
    <dl class="client-deactivation__stamp" *ngIf="isActive && reactivationStamp">
      <div class="client-deactivation__stamp-row" *ngIf="reactivatedAt">
        <dt>Reactivated</dt>
        <dd>{{ reactivatedAt | date: 'medium' }}</dd>
      </div>
      <div class="client-deactivation__stamp-row" *ngIf="reactivationStamp.reactivated_by_name || reactivationStamp.reactivated_by">
        <dt>By</dt>
        <dd>{{ reactivationStamp.reactivated_by_name || reactivationStamp.reactivated_by }}</dd>
      </div>
      <div class="client-deactivation__stamp-row" *ngIf="deactivatedAt">
        <dt>Last deactivated</dt>
        <dd>{{ deactivatedAt | date: 'medium' }}</dd>
      </div>
    </dl>

    <p class="client-deactivation__hint" *ngIf="!isActive">
      Reactivating the client does not re-enroll these patients — each one has to be re-enrolled
      individually.
    </p>
  </mat-card-content>
  <mat-card-actions>
    <div class="client-deactivation__actions">
      <button
        *ngIf="isActive"
        mat-raised-button
        color="warn"
        type="button"
        [disabled]="!clientId || isBusy"
        matTooltip="Deactivate this client"
        (click)="confirmDeactivation()"
      >
        {{ hasFailedRun ? 'Retry Deactivation' : 'Deactivate Client' }}
      </button>
      <!-- Only the client flag comes back; the confirmation says the patients stay unenrolled. -->
      <button
        *ngIf="!isActive"
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="!clientId || isBusy"
        matTooltip="Make this client active again. Its patients are not re-enrolled."
        (click)="confirmReactivation()"
      >
        Activate Client
      </button>
      <mat-spinner [diameter]="20" *ngIf="isCountingPatients || isStarting || isReactivating"></mat-spinner>
    </div>
  </mat-card-actions>
</mat-card>
